import { Style } from '@/components/style';
import {
  useContext,
  useEffect,
  useState,
  useRef,
  MutableRefObject,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import StoreUtils from '@/utils/storeInfoUtils';
import { isEmpty, cloneDeep } from 'lodash';
import OrderServeType from '@/constants/OrderServeType';
import UberDirect from '@/utils/uberDirect';
import {
  LocationSelector,
  AddressSelector,
  AddressDetails,
  DeliveryMethodSelector,
} from '@/components/welcome/components';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { setWelcomeCustomerAddress } from '@/actions/customizeWelcome/customizeWelcomeAction';
import { ReactComponent as Warn } from '@/assets/img/warn.svg';
import { ReactComponent as TimeClock } from '@/assets/img/time_clock.svg';
import { ReactComponent as Address } from '@/assets/img/address.svg';
import Validator from '@/utils/validator';
import { TW_address } from '@/constants/Address';
import {
  setSelectCity,
  setSelectArea,
  setSelectStreet,
} from '@/actions/uberDirect/uberDirectAction';

const { alertColor, blackColor, primaryColor, gray3Color } =
  new Style().getUiConfig();

function WelcomeAddress({
  webStoreVo,
  orderVo,
  selectedServeType,
  selectedTakeOutServeType,
  setSelectedTakeOutServeType,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [address, setAddress] = useState('');
  const [tradingLocations, setTradingLocations] = useState([]);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [isLocationSelectorAddressVaild, setIsLocationSelectorAddressVaild] =
    useState(true);
  const [isAddressVaild, setIsAddressVaild] = useState(true);
  const [addressVaildatorMessage, setAddressVaildatorMessage] = useState('');
  // uber direct 串接 --- start ---
  const addressInput = useRef<HTMLInputElement>(
    null
  ) as MutableRefObject<HTMLInputElement>;
  const isUberDirectPlatform =
    UberDirect.UberDirectStatus.isCustomizeWelcomeUberDirectPlatform(
      webStoreVo,
      selectedServeType
    );
  const [deliverableAction, setDeliverableAction] = useState<boolean>(true);
  const [city, setCity] = useState<string | null>(null);
  const [area, setArea] = useState<string | null>(null);
  const [street, setStreet] = useState<string | null>(null);
  const [changeAddress, setChangeAddress] = useState<boolean>(false);

  const isCustomerAddressValid = useSelector(
    (state: RootStateOrAny) =>
      state.customizeWelcomeReducer.isCustomerAddressValid
  );

  useEffect(() => {
    setIsAddressVaild(isCustomerAddressValid);
    setIsLocationSelectorAddressVaild(isCustomerAddressValid);

    if (!isCustomerAddressValid && isEmpty(address)) {
      setAddressVaildatorMessage(
        intl.formatMessage({
          id: `delivery.please.input.address`,
        })
      );
    }
  }, [isCustomerAddressValid]);

  useEffect(() => {
    if (isUberDirectPlatform) {
      // 初始化 外送方式
      const tempDeliverableAction =
        UberDirect.UberDirectStatus.getBooleanOfDeliverableAction(orderVo);
      if (tempDeliverableAction !== null) {
        setDeliverableAction(tempDeliverableAction);
      }
    }
  }, []);

  const initAreaData = (cityName) => {
    const areas = cloneDeep(
      TW_address.filter((data) => data.name === cityName)[0].districts
    );
    const tempAreaData = areas.map((data) => {
      return data.name;
    });
    return tempAreaData;
  };
  useEffect(() => {
    const cityData = TW_address.map((data) => {
      return data.name;
    });
    const selectCity = cityData[0];
    setCity(selectCity);
    dispatch(setSelectCity(selectCity));
    const selectArea = initAreaData(selectCity)[0];
    setArea(selectArea);
    dispatch(setSelectArea(selectArea));
  }, []);

  useEffect(() => {
    dispatch(setSelectStreet(street));
    dispatch(setWelcomeCustomerAddress(street));
  }, [street]);

  // uber direct 串接 --- end ---

  // 下拉地址選單
  useEffect(() => {
    if (changeAddress && isEmpty(address)) return;
    setIsLocationSelectorAddressVaild(true);
  }, [address]);

  // 一般地址輸入匡
  useEffect(() => {
    if (!changeAddress) {
      return;
    }
    if (textareaRef && textareaRef.current) {
      textareaRef.current.style.height = '0px';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = scrollHeight + 'px';
    }

    const isAddressValid = Validator.addressValidator(address, true);
    setIsAddressVaild(isAddressValid);
    if (isEmpty(address)) {
      setAddressVaildatorMessage(
        intl.formatMessage({
          id: `delivery.please.input.address`,
        })
      );
    }
  }, [address]);

  useEffect(() => {
    dispatch(setWelcomeCustomerAddress(address));
  }, [address]);

  const onChangeAddress = (e) => {
    setChangeAddress(true);
    setAddress(e.target.value);
  };

  return (
    <div>
      {/* uber direct */}
      {selectedServeType === OrderServeType.DELIVERY && isUberDirectPlatform && (
        <div style={{ marginBottom: '10px' }}>
          <AddressSelector
            city={city}
            setCity={setCity}
            area={area}
            setArea={setArea}
          />
          <AddressDetails
            addressRef={addressInput}
            street={street}
            setStreet={setStreet}
            selectedServeType={selectedServeType}
          />
          <DeliveryMethodSelector
            deliveryMethod={deliverableAction}
            setDeliveryMethod={setDeliverableAction}
          />
        </div>
      )}

      {/* 地址一般輸入 */}
      {selectedServeType === OrderServeType.DELIVERY &&
        !isUberDirectPlatform &&
        isEmpty(webStoreVo?.tradingLocations) && (
          <div>
            <div className="d-flex justify-content-center">
              <textarea
                ref={textareaRef}
                value={address}
                style={{
                  width: '100%',
                  minHeight: '42px',
                  fontSize: '16px',
                  borderRadius: '10px',
                  border: `1px solid`,
                  borderColor: gray3Color,
                  paddingLeft: '15px',
                  paddingTop: '8px',
                  display: 'block',
                  resize: 'none',
                }}
                onChange={onChangeAddress}
                data-testid="address"
                maxLength={255}
                placeholder={intl.formatMessage({
                  id: 'welcome.address',
                })}
              />
            </div>
            {!isAddressVaild ? (
              <div
                className="d-flex align-items-center"
                style={{
                  color: alertColor,
                  fontSize: '12px',
                  margin: '5px 0px 10px 0px',
                }}
              >
                <Warn style={{ marginRight: '5px' }} />
                {addressVaildatorMessage}
              </div>
            ) : (
              <div style={{ marginBottom: '10px' }} />
            )}
          </div>
        )}

      {/* 地址下拉式選單 */}
      {!isEmpty(webStoreVo?.tradingLocations) &&
        selectedServeType === OrderServeType.DELIVERY &&
        !isUberDirectPlatform && (
          <div style={{ marginBottom: '10px' }}>
            <LocationSelector
              tradingLocation={address}
              setTradingLocation={setAddress}
              locations={webStoreVo.tradingLocations}
              showWarnMsg={!isLocationSelectorAddressVaild}
            />
            {!isLocationSelectorAddressVaild ? (
              <div
                className="d-flex align-items-center"
                style={{
                  color: alertColor,
                  fontSize: '12px',
                  margin: '5px 0px 10px 0px',
                }}
              >
                <Warn style={{ marginRight: '5px' }} />
                {intl.formatMessage({
                  id: 'delivery.please.input.address.warn',
                })}
              </div>
            ) : (
              <div style={{ marginBottom: '10px' }} />
            )}
          </div>
        )}
    </div>
  );
}

WelcomeAddress.defaultProps = {};

WelcomeAddress.propTypes = {
  webStoreVo: PropTypes.shape({
    availableServeTypes: PropTypes.arrayOf(PropTypes.string),
    availableTakeOutServeTypes: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  orderVo: PropTypes.shape({}).isRequired,
  selectedServeType: PropTypes.string.isRequired,
  selectedTakeOutServeType: PropTypes.string.isRequired,
  setSelectedTakeOutServeType: PropTypes.func.isRequired,
};

export default WelcomeAddress;
